import React, { useState, useEffect } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
  Typography, Paper, CircularProgress, IconButton, Tooltip, Pagination,
  InputAdornment,
  DialogContentText,
  Radio,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Edit, ToggleOn, ToggleOff, Delete, Search as SearchIcon, VpnKey, Start, Star, EmojiEvents, Diamond, Subscriptions, WorkspacePremium, Upload, AddCircleOutline } from '@mui/icons-material';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { BACKEND_BASE_URL } from '../config'; // Removed unused CAPTURE_BACKEND_URL
import api from '../components/api';
import ChatIcon from '@mui/icons-material/Chat';
import { Lightbulb } from '@mui/icons-material';
import MicIcon from '@mui/icons-material/Mic';
import { Box, Checkbox } from '@mui/material';
import { Switch, FormControlLabel } from '@mui/material';

export default function AccountsPage() {
  // All users fetched from the API
  const [users, setUsers] = useState([]);
  // Currently selected user for edit or delete
  const [selectedUser, setSelectedUser] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);

  // Frontend pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12; // Items per page

  // Search state for filtering (client‑side)
  const [searchQuery, setSearchQuery] = useState('');
  const [userCount, setUserCount] = useState(0);
  const [awareDialogOpen, setAwareDialogOpen] = useState(false);
  const [toggledTowns, setToggledTowns] = useState([]);

  const [selectedTown, setSelectedTown] = useState(null);

  const [townSearchQuery, setTownSearchQuery] = useState('')

  // Add User dialog state
  const [openAddUser, setOpenAddUser] = useState(false);
  const [userForm, setUserForm] = useState({
    email: "",
    name: "",
    password: "",
    role: "USER",
    is_partner: false,
    phone_number: "",
    credits: 100
  });
  const [addingUser, setAddingUser] = useState(false);

  // Toggle checkbox state


  // Save selected towns to backend
  // Save selected towns to backend
  const handleSaveSelectedTowns = async () => {
    try {
      const token = localStorage.getItem("authToken");

      if (!token) {
        toast.error("Token not found. Please log in again.");
        return;
      }

      if (!selectedUser) {
        toast.error("No user is selected for this action.");
        return;
      }

      const userId = selectedUser.id;

      // Find the selected town object
      const selectedTownObj = toggledTowns.find(
        (town) => town.name === selectedTown
      );

      const payload = {
        user_id: userId,
        municipalities: selectedTownObj ? [selectedTownObj.id] : [],
      };

      console.log("Sending payload:", payload);

      await api.post(
        `${BACKEND_BASE_URL}/api/capture/user-capture-access/`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      toast.success("Towns saved successfully.");

      // ✅ Update local state immediately (checkbox + saved municipalities)
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === selectedUser.id
            ? {
              ...user,
              has_capture_access: !!selectedTown, // true if a town is selected
              capture_municipalities: selectedTownObj
                ? [selectedTownObj.id]
                : [],
            }
            : user
        )
      );

      // Close dialog + reset
      setAwareDialogOpen(false);
      setSelectedTown(null);
      setSelectedUser(null);
    } catch (error) {
      console.error("Save Error:", error);
      toast.error(
        error?.response?.data?.message || "Failed to save selected towns."
      );
    }
  };
  const handleToggleDemoLinksAccess = async (user) => {
    try {
      const updatedStatus = !user.demo_links_access;
      await api.patch(`${BACKEND_BASE_URL}/api/admin-users/${user.id}/`, {
        demo_links_access: updatedStatus,
      });
      // update local state
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, demo_links_access: updatedStatus } : u));
      toast.success(`Demo Links access ${updatedStatus ? 'granted' : 'revoked'} for ${user.email}`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to update demo links access.');
    }
  };

  const handleTogglePartner = async (user) => {
    try {
      const updatedStatus = !user.is_partner;
      await api.patch(`${BACKEND_BASE_URL}/api/admin-users/${user.id}/`, {
        is_partner: updatedStatus,
      });
      setUsers(prev => prev.map(u => (u.id === user.id ? { ...u, is_partner: updatedStatus } : u)));
      toast.success(
        `Town partner permission ${updatedStatus ? 'enabled' : 'disabled'} for ${user.email}`
      );
    } catch (err) {
      console.error(err);
      toast.error('Failed to update town partner permission.');
    }
  };

  // Handle role change when clicking on "-" in Bulk Import or Demo Link Access columns
  const handleRoleChange = async (user, newRole) => {
    try {
      const updateData = { role: newRole };
      
      // If changing to DEMO role, enable demo_links_access by default
      if (newRole === 'DEMO') {
        updateData.demo_links_access = true;
      }
      
      await api.patch(`${BACKEND_BASE_URL}/api/admin-users/${user.id}/`, updateData);
      
      // Update local state
      setUsers(prev => prev.map(u => 
        u.id === user.id 
          ? { ...u, role: newRole, demo_links_access: newRole === 'DEMO' ? true : u.demo_links_access }
          : u
      ));
      
      toast.success(`User ${user.email} role changed to ${newRole}`);
    } catch (err) {
      console.error(err);
      const errorMsg = err?.response?.data?.detail || err?.response?.data?.message || 'Failed to update user role';
      toast.error(`Error: ${errorMsg}`);
    }
  };

  const handleAddUser = async () => {
    console.log("handleAddUser called", userForm);
    if (!userForm.email || !userForm.name || !userForm.password) {
      toast.error("Email, name, and password are required.");
      return;
    }

    setAddingUser(true);
    try {
      console.log("Calling API:", `${BACKEND_BASE_URL}/api/demo/admin/add-user/`, userForm);
      const res = await api.post(`${BACKEND_BASE_URL}/api/demo/admin/add-user/`, userForm);
      console.log("User created successfully:", res.data);
      toast.success(`User created successfully! Email: ${res.data.email}`);
      setOpenAddUser(false);
      setUserForm({
        email: "",
        name: "",
        password: "",
        role: "USER",
        is_partner: false,
        phone_number: "",
        credits: 100
      });
      // Refresh users list
      fetchAllUsers();
    } catch (err) {
      console.error("Failed to create user:", err);
      const errorMsg = err?.response?.data?.detail || err?.response?.data?.error || "Failed to create user";
      toast.error(`Error: ${errorMsg}`);
    } finally {
      setAddingUser(false);
    }
  };


  // Function to fetch toggled towns
  // Function to fetch toggled towns
  const fetchToggledTowns = async (user) => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        toast.error("No token found. Please log in.");
        return;
      }

      // Fetch all available municipalities
      const response = await api.get(
        `${BACKEND_BASE_URL}/api/capture/capture-municipalities/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (Array.isArray(response.data.data)) {
        setToggledTowns(response.data.data);

        // ✅ Preselect the user's assigned municipality (if any)
        let userMunicipality = null;
        if (Array.isArray(user.capture_municipalities)) {
          userMunicipality = response.data.data.find((town) =>
            user.capture_municipalities.includes(town.id)
          );
        }

        setSelectedTown(userMunicipality ? userMunicipality.name : null);
        setAwareDialogOpen(true);
      } else {
        toast.error("Unexpected response format.");
      }
    } catch (error) {
      toast.error("Failed to fetch toggled towns. Unauthorized.");
      console.error("API Error:", error);
    }
  };


  // ================================================================


  // On mount, fetch all users and the current user
  useEffect(() => {
    fetchAllUsers();
    fetchCurrentUser();
  }, []);

  // Fetch current logged-in user (to prevent self‑deletion, etc.)
  const fetchCurrentUser = async () => {
    try {
      const response = await api.get(`${BACKEND_BASE_URL}/api/admin-user/current_user/`);
      setCurrentUserId(response.data.id);
    } catch (error) {
      toast.error('Failed to fetch current user.');
    }
  };

  // Fetch all users by using a large page_size so we get all records in one request.
  const fetchAllUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get(`${BACKEND_BASE_URL}/api/admin-users/?page=1&page_size=100`);
      const { results, count } = response.data;
      setUsers(Array.isArray(results) ? results : []);
      setUserCount(count || 0);
    } catch (error) {
      toast.error('Failed to fetch users.');
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on the search query (case-insensitive)
  const filteredUsers = users.filter((user) => {
    const query = searchQuery.toLowerCase();
    return (
      (user.name && user.name.toLowerCase().includes(query)) ||
      (user.email && user.email.toLowerCase().includes(query)) ||
      (user.phone_number && user.phone_number.toLowerCase().includes(query))
    );
  });

  // Calculate total pages based on the filtered users
  const totalPages = Math.ceil(filteredUsers.length / pageSize);

  // Get only the users for the current page
  const paginatedUsers = filteredUsers.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Update current page when pagination changes
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Toggle the active status of a chat
  const handleToggleChat = async (user) => {
    try {
      const updatedStatus = !user.allow_chat;
      await api.patch(`${BACKEND_BASE_URL}/api/admin-users/${user.id}/`, {
        allow_chat: updatedStatus,
      });
      toast.success(`User Chat has been ${updatedStatus ? 'activated' : 'disabled'}  successfully.`);
      setUsers((prevUsers) =>
        prevUsers.map((u) => (u.id === user.id ? { ...u, allow_chat: updatedStatus } : u))
      );
    } catch (error) {
      toast.error('Failed to update account status.');
    }
  };

  // Toggle the active status of a chat
  const handleToggleImport = async (user) => {
    try {
      const updatedStatus = !user.only_import;
      await api.patch(`${BACKEND_BASE_URL}/api/admin-users/${user.id}/`, {
        only_import: updatedStatus,
      });
      toast.success(`User Import Status has been ${updatedStatus ? 'activated' : 'disabled'}  successfully.`);
      setUsers((prevUsers) =>
        prevUsers.map((u) => (u.id === user.id ? { ...u, only_import: updatedStatus } : u))
      );
    } catch (error) {
      toast.error('Failed to update account status.');
    }
  };

  // Toggle the active status of a user
  const handleToggleAccount = async (user) => {
    try {
      const updatedStatus = !user.is_active;
      await api.patch(`${BACKEND_BASE_URL}/api/admin-users/${user.id}/`, {
        is_active: updatedStatus,
      });
      toast.success(`User has been ${updatedStatus ? 'activated' : 'disabled'} successfully.`);
      setUsers((prevUsers) =>
        prevUsers.map((u) => (u.id === user.id ? { ...u, is_active: updatedStatus } : u))
      );
    } catch (error) {
      toast.error('Failed to update account status.');
    }
  };

  const handleToggleExplain = async (user) => {
    try {
      const updatedStatus = !user.aware_explain;
      await api.patch(`${BACKEND_BASE_URL}/api/admin-users/${user.id}/`, {
        aware_explain: updatedStatus,
      });
      toast.success(`User Explain has been ${updatedStatus ? 'enabled' : 'disabled'} successfully.`);
      setUsers((prevUsers) =>
        prevUsers.map((u) => (u.id === user.id ? { ...u, aware_explain: updatedStatus } : u))
      );
    } catch (error) {
      toast.error('Failed to update Explain status.');
    }
  };


  // Open the edit dialog with a shallow copy of the selected user
  const handleEditUser = async (user) => {
    try {
      // Fetch user's current explain credits
      const creditsResponse = await api.get(`${BACKEND_BASE_URL}/api/admin-users/${user.id}/get_credits/`);
      const credits = creditsResponse.data.credits_remaining || 0;
      
      setSelectedUser({ 
        ...user, 
        credits: credits 
      });
      setEditDialogOpen(true);
    } catch (error) {
      // If fetch fails, just use 0
      console.error("Failed to fetch credits:", error);
      setSelectedUser({ ...user, credits: 0 });
      setEditDialogOpen(true);
    }
  };

  // Save the edited user details
  const handleSaveChanges = async () => {
    if (!selectedUser) return;
    try {
      // Prepare update data
      const updateData = {
        name: selectedUser.name,
        role: selectedUser.role,
        credits: selectedUser.credits || 0,
        is_partner: !!selectedUser.is_partner,
      };
      if (selectedUser.role === 'DEMO') {
        updateData.demo_links_access = !!selectedUser.demo_links_access;
      }
      
      // Update user and credits
      await api.patch(`${BACKEND_BASE_URL}/api/admin-users/${selectedUser.id}/`, updateData);
      toast.success('User updated successfully.');
      setUsers((prevUsers) =>
        prevUsers.map((u) => (u.id === selectedUser.id ? { ...u, ...selectedUser } : u))
      );
      setEditDialogOpen(false);
      setSelectedUser(null);
    } catch (error) {
      console.error("Update error:", error);
      toast.error('Failed to save changes.');
    }
  };

  // Delete the selected user
  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    try {
      await api.delete(`${BACKEND_BASE_URL}/api/admin-users/${selectedUser.id}/`);
      toast.success('User has been deleted successfully.');
      setUsers((prevUsers) => prevUsers.filter((u) => u.id !== selectedUser.id));
      setDeleteDialogOpen(false);
      setSelectedUser(null);
    } catch (error) {
      toast.error('Failed to delete user.');
    }
  };

  const filteredTowns = toggledTowns.filter((town) =>
    town.town_code.toLowerCase().includes(townSearchQuery.toLowerCase())
  );
  const handleToggleAccessWithoutSubscription = async (user) => {
    try {
      const updatedStatus = !user.access_without_subscription;

      await api.patch(`${BACKEND_BASE_URL}/api/admin-users/${user.id}/`, {
        access_without_subscription: updatedStatus,
      });

      setUsers(prevUsers =>
        prevUsers.map(u =>
          u.id === user.id ? { ...u, access_without_subscription: updatedStatus } : u
        )
      );
      toast.success(`Access without subscription has been ${updatedStatus ? 'granted' : 'revoked'} successfully.`);
    } catch (err) {
      toast.error("Failed to update access without subscription.");
    }
  };



  const headerCellStyle = {
    fontWeight: "bold",
    backgroundColor: "#152F8F",
    color: "white",
    border: "1px solid #ddd",
    minWidth: 100,
    fontSize: "1rem"
  };

  return (
    <div>
      {/* Header with Title and Search Box on the same row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <Typography variant="h4">
          Manage User Accounts
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Typography variant="h6">Total Users: {userCount}</Typography>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1); // Reset to first page on search change
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            style={{ width: '300px' }}
          />
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => {
              console.log("Add User button clicked");
              setOpenAddUser(true);
            }}
          >
            Add User
          </Button>
        </Box>
      </div>

      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell style={headerCellStyle} >Name</TableCell>
                  <TableCell style={headerCellStyle}>Email</TableCell>
                  <TableCell style={headerCellStyle}>Role</TableCell>
                  <TableCell style={headerCellStyle}>Status</TableCell>
                  <TableCell align="center" style={headerCellStyle}>Actions</TableCell>
                  <TableCell style={headerCellStyle}>Bulk Import</TableCell>
                  <TableCell style={headerCellStyle}>Demo Link Access</TableCell>
                  <TableCell style={headerCellStyle}>Town partner</TableCell>

                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.name || 'N/A'}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 'bold',
                        color: user.role === 'DEMO' ? '#ff9800' : 
                               user.role === 'BULK_IMPORT' ? '#2196f3' : 
                               user.role === 'ADMIN' ? '#f44336' : '#4caf50'
                      }}>
                        {user.role || 'USER'}
                      </Typography>
                    </TableCell>
                    <TableCell>{user.is_active ? 'Active' : 'Inactive'}</TableCell>
                    <TableCell>
                      <Tooltip title="Edit">
                        <IconButton onClick={() => handleEditUser(user)}>
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={user.allow_chat ? 'Chat Enabled' : 'Chat Disabled'}>
                        <IconButton onClick={() => handleToggleChat(user)} sx={{
                          fontSize: "100%",
                          color: user.allow_chat ? "#46d633" : "gray", // Green for active, Gray for inactive
                          marginLeft: "8px",
                        }}>

                          <ChatIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title={user.aware_explain ? 'Explanation Enabled' : 'Explanation Disabled'}>
                        <IconButton
                          onClick={() => handleToggleExplain(user)}
                          sx={{
                            fontSize: "100%",
                            color: user.aware_explain ? "#fbc02d" : "gray", // Yellow for active, Gray for inactive
                            marginLeft: "8px",
                          }}
                        >
                          <Lightbulb />

                        </IconButton>
                      </Tooltip>



                      <Tooltip title={user.is_active ? 'Disable' : 'Enable'}>
                        <IconButton
                          onClick={() => handleToggleAccount(user)}
                          color={user.is_active ? 'primary' : 'error'}
                          disabled={user.id === currentUserId}
                        >
                          {user.is_active ? <ToggleOn sx={{
                            fontSize: "100%",
                          }} /> : <ToggleOff sx={{
                            fontSize: "100%",
                          }} />}
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Delete">
                        <IconButton
                          onClick={() => {
                            setSelectedUser(user);
                            setDeleteDialogOpen(true);
                          }}
                          disabled={user.id === currentUserId}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={user.access_without_subscription ? 'User without subscription enabled' : 'User Subscription is required'}>
                        <IconButton
                          onClick={() => handleToggleAccessWithoutSubscription(user)}
                          sx={{
                            fontSize: "100%",
                            color: user.access_without_subscription ? "#6a1b9a" : "gray",
                            marginLeft: "8px",
                          }}
                        >
                          <WorkspacePremium /> {/* Or any suitable icon */}
                        </IconButton>
                      </Tooltip>



                      {/* aware capture */}
                      <Tooltip title="Aware Capture">
                        <Checkbox
                          checked={user.has_capture_access}   // ✅ will update immediately
                          onChange={() => {
                            setSelectedUser(user);
                            fetchToggledTowns(user);
                          }}
                          icon={<MicIcon sx={{ color: 'gray', fontSize: 20, fill: 'gray' }} />}
                          checkedIcon={<MicIcon sx={{ color: '#1976d2', fontSize: 20, fill: '#1976d2' }} />}
                          inputProps={{ 'aria-label': 'toggle capture access' }}
                          sx={{ ml: 1, p: 0 }}
                        />

                      </Tooltip>

                    </TableCell>

                    {/* <TableCell>
                      <span onClick={() => handleToggleImport(user)}>{user.only_import ? '✔️' : '❌'}</span>

                    </TableCell> */}
                    <TableCell>
                      {user.role === 'BULK_IMPORT' ? (
                        <Tooltip title="Bulk Import User">
                          <IconButton
                            size="small"
                            sx={{
                              color: '#152F8F',
                              '&:hover': {
                                backgroundColor: 'rgba(21, 47, 143, 0.1)',
                              },
                            }}
                          >
                            <Upload />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Click to assign BULK_IMPORT role">
                          <IconButton
                            size="small"
                            onClick={() => handleRoleChange(user, 'BULK_IMPORT')}
                            sx={{
                              color: '#999',
                              '&:hover': {
                                color: '#152F8F',
                                backgroundColor: 'rgba(21, 47, 143, 0.1)',
                              },
                            }}
                          >
                            <AddCircleOutline />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>


                    {/* put this inside the <TableRow key={user.id}> after your other TableCell blocks */}
                    <TableCell>
                      {user.role === 'DEMO' ? (
                        <Tooltip title={user.demo_links_access ? 'Demo Access enabled' : 'Demo Access disabled'}>
                          <Switch
                            checked={!!user.demo_links_access}
                            onChange={() => handleToggleDemoLinksAccess(user)}
                            size="small"
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: '#152F8F',
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#152F8F',
                              },
                            }}
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Click to assign DEMO role">
                          <IconButton
                            size="small"
                            onClick={() => handleRoleChange(user, 'DEMO')}
                            sx={{
                              color: '#999',
                              '&:hover': {
                                color: '#152F8F',
                                backgroundColor: 'rgba(21, 47, 143, 0.1)',
                              },
                            }}
                          >
                            <AddCircleOutline />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>

                    <TableCell align="center">
                      <Tooltip title={user.is_partner ? 'Town partner (portal) enabled' : 'Town partner (portal) disabled'}>
                        <Switch
                          checked={!!user.is_partner}
                          onChange={() => handleTogglePartner(user)}
                          size="small"
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#2e7d32',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#2e7d32',
                            },
                          }}
                        />
                      </Tooltip>
                    </TableCell>

                  </TableRow>
                ))}
                {paginatedUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography align="center">No users found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              style={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}
            />
          )}
        </>
      )}

      {/* Edit User Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedUser(null);
        }}
      >
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <>
              <TextField
                fullWidth
                margin="dense"
                label="Name"
                value={selectedUser.name || ''}
                onChange={(e) =>
                  setSelectedUser({ ...selectedUser, name: e.target.value })
                }
              />
              <TextField
                fullWidth
                margin="dense"
                label="Email"
                value={selectedUser.email || ''}
                disabled
              />
              <TextField
                label="Credits"
                type="number"
                value={selectedUser.credits || 0}
                onChange={(e) =>
                  setSelectedUser({ ...selectedUser, credits: parseInt(e.target.value) || 0 })
                }
                fullWidth
                required
                margin="dense"
                inputProps={{ min: 0 }}
                helperText="Number of explain credits for this user"
              />
              <FormControl fullWidth margin="dense">
                <InputLabel>Role</InputLabel>
                <Select
                  value={selectedUser.role || 'USER'}
                  label="Role"
                  onChange={(e) => {
                    const newRole = e.target.value;
                    setSelectedUser({ 
                      ...selectedUser, 
                      role: newRole,
                      // If changing to DEMO, enable demo_links_access by default
                      demo_links_access: newRole === "DEMO" ? (selectedUser.demo_links_access !== undefined ? selectedUser.demo_links_access : true) : selectedUser.demo_links_access
                    });
                  }}
                >
                  <MenuItem value="USER">USER</MenuItem>
                  <MenuItem value="DEMO">DEMO</MenuItem>
                  <MenuItem value="BULK_IMPORT">BULK_IMPORT</MenuItem>
                  <MenuItem value="ADMIN">ADMIN</MenuItem>
                </Select>
              </FormControl>
              {selectedUser.role === "DEMO" && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!selectedUser.demo_links_access}
                      onChange={(e) => setSelectedUser({ ...selectedUser, demo_links_access: e.target.checked })}
                    />
                  }
                  label="Demo Links Access"
                />
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={!!selectedUser.is_partner}
                    onChange={(e) => setSelectedUser({ ...selectedUser, is_partner: e.target.checked })}
                  />
                }
                label="Town partner (portal permission)"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditDialogOpen(false);
              setSelectedUser(null);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleSaveChanges} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedUser(null);
        }}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this user?</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setSelectedUser(null);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* aware capture */}
      <Dialog
        open={awareDialogOpen}
        onClose={() => setAwareDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 3,
            gap: 2,
          }}
        >
          <Typography variant="h5" sx={{ whiteSpace: "nowrap" }}>
            Select Towns for Aware Capture
          </Typography>

          <TextField
            label="Search towns"
            variant="outlined"
            size="small"
            value={townSearchQuery}
            onChange={(e) => setTownSearchQuery(e.target.value)}
            sx={{ minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>


        <DialogContent dividers sx={{ p: 3 }}>
          {filteredTowns.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead sx={{ backgroundColor: "#152F8F" }}>
                  <TableRow>
                    <TableCell sx={{ padding: "12px", fontWeight: "bold", color: "white" }}>
                      State/County
                    </TableCell>
                    <TableCell sx={{ padding: "12px", fontWeight: "bold", color: "white" }}>
                      Municipality
                    </TableCell>
                    <TableCell sx={{ padding: "12px", fontWeight: "bold", color: "white" }}>
                      Allow access
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTowns.map((town, index) => (
                    <TableRow key={index}>
                      <TableCell
                        sx={{
                          backgroundColor: index % 2 === 0 ? "#E3F2FD" : "white",
                          border: "1px solid #E0E0E0",
                          padding: "12px",
                        }}
                      >
                        {town.state}/{town.county}
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: index % 2 === 0 ? "#E3F2FD" : "white",
                          border: "1px solid #E0E0E0",
                          padding: "12px",
                        }}
                      >
                        {town.name}
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: index % 2 === 0 ? "#E3F2FD" : "white",
                          border: "1px solid #E0E0E0",
                          padding: "12px",
                        }}
                      >
                        <Radio
                          checked={selectedTown === town.name}
                          onChange={() => setSelectedTown(town.name)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <DialogContentText sx={{ mt: 2 }}>
              {townSearchQuery
                ? `No towns found for "${townSearchQuery}".`
                : "No toggled towns found."}
            </DialogContentText>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAwareDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveSelectedTowns}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog 
        open={openAddUser} 
        onClose={() => {
          console.log("Dialog close clicked");
          setOpenAddUser(false);
        }} 
        fullWidth 
        maxWidth="sm"
      >
        <DialogTitle>Add User</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Email"
              type="email"
              value={userForm.email}
              onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
              fullWidth
              required
              margin="dense"
            />
            <TextField
              label="Name"
              value={userForm.name}
              onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
              fullWidth
              required
              margin="dense"
            />
            <TextField
              label="Password"
              type="password"
              value={userForm.password}
              onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
              fullWidth
              required
              margin="dense"
            />
            <PhoneInput
              country="us"
              value={userForm.phone_number || ''}
              onChange={(value) => setUserForm({ ...userForm, phone_number: value })}
              inputStyle={{ width: '100%' }}
            />
            <FormControl fullWidth margin="dense">
              <InputLabel>Role</InputLabel>
              <Select
                value={userForm.role}
                label="Role"
                onChange={(e) => {
                  setUserForm({
                    ...userForm,
                    role: e.target.value
                  });
                }}
              >
                <MenuItem value="USER">USER</MenuItem>
                <MenuItem value="DEMO">DEMO</MenuItem>
                <MenuItem value="BULK_IMPORT">BULK_IMPORT</MenuItem>
                <MenuItem value="ADMIN">ADMIN</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="dense">
              <InputLabel>Town partner permission</InputLabel>
              <Select
                value={userForm.is_partner ? "YES" : "NO"}
                label="Town partner permission"
                onChange={(e) =>
                  setUserForm({
                    ...userForm,
                    is_partner: e.target.value === "YES",
                  })
                }
              >
                <MenuItem value="NO">NO</MenuItem>
                <MenuItem value="YES">YES</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Credits"
              type="number"
              value={userForm.credits}
              onChange={(e) => setUserForm({ ...userForm, credits: parseInt(e.target.value) || 0 })}
              fullWidth
              required
              margin="dense"
              inputProps={{ min: 0 }}
              helperText="Number of explain credits to assign to this user"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddUser(false)} disabled={addingUser}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddUser}
            disabled={addingUser || !userForm.email || !userForm.name || !userForm.password}
          >
            {addingUser ? "Creating..." : "Create User"}
          </Button>
        </DialogActions>
      </Dialog>

    </div>
  );
}
